"use client";

import type { Vehicle } from "@/types/fleet";
import {
  formatVehicleSelectLabel,
  getVehicleDeviceIdDisplay,
  getVehicleMakeModel,
  getVehiclePlateDisplay,
} from "@/lib/fleet/vehicle-select";
import { resolveDriverDisplay } from "@/lib/fleet/driver-ref";
import { DriverLabel } from "@/components/fleet/DriverLabel";
import { PlateNumber } from "@/components/fleet/PlateNumber";

interface VehicleSelectLabelProps {
  vehicle: Vehicle;
  /**
   * Resolves the vehicle's `driverId` to a name. Omit to leave the driver off entirely — the label
   * degrades to plate and make/model rather than showing a raw id.
   */
  driverNamesById?: Map<string, string>;
}

/**
 * One dropdown row: device id, plate, make and model, current driver.
 *
 * Plate and make/model stay the visual anchors — the two flanking fields are muted and shrink
 * first, so a narrow trigger truncates the driver rather than the registration. Every part is
 * optional; a vehicle with none of them falls back to the plain-text label.
 */
export function VehicleSelectLabel({ vehicle, driverNamesById }: VehicleSelectLabelProps) {
  const deviceId = getVehicleDeviceIdDisplay(vehicle);
  const plate = getVehiclePlateDisplay(vehicle);
  const makeModel = getVehicleMakeModel(vehicle);
  const driver = driverNamesById ? resolveDriverDisplay(vehicle, driverNamesById) : null;

  let core;
  if (plate && makeModel) {
    core = (
      <>
        <PlateNumber>{plate}</PlateNumber>
        <span> - </span>
        <span className="truncate">{makeModel}</span>
      </>
    );
  } else if (plate) {
    core = <PlateNumber>{plate}</PlateNumber>;
  } else if (makeModel) {
    core = <span className="truncate">{makeModel}</span>;
  } else {
    core = <span className="truncate">{formatVehicleSelectLabel(vehicle)}</span>;
  }

  return (
    <>
      {deviceId && (
        <span className="shrink-0 font-mono text-xs text-muted-foreground">{deviceId}</span>
      )}
      {core}
      {driver && (
        <>
          <span className="shrink-0 text-muted-foreground">·</span>
          <DriverLabel display={driver} className="min-w-0 text-xs text-muted-foreground" />
        </>
      )}
    </>
  );
}
